import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Wind } from 'lucide-react';
import BreathingCircle from '@/components/BreathingCircle';
import LegalFooter from '@/components/LegalFooter';

export default function Landing() {
    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-sage/40 blur-3xl" />
            <div className="pointer-events-none absolute top-1/3 -right-40 w-[460px] h-[460px] rounded-full bg-dusty/50 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 left-1/4 w-[460px] h-[460px] rounded-full bg-lavender-soft/50 blur-3xl" />

            <header className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <img
                        src="/qm-logo.png"
                        alt="Quiet Mind"
                        className="w-10 h-10 rounded-full object-cover bg-black shrink-0"
                    />
                    <span className="font-serif text-xl text-forest">Quiet Mind</span>
                </div>
                <Link to="/auth" data-testid="landing-signin-link" className="qm-btn-ghost text-sm">Sign in</Link>
            </header>

            <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 pb-24 grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-7 animate-fade-in">
                    <span className="inline-block text-xs uppercase tracking-[0.25em] text-forest-soft">A meditation companion</span>
                    <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-forest">
                        A quieter mind, <em className="not-italic text-forest-soft">on your own terms.</em>
                    </h1>
                    <p className="text-lg text-forest-soft max-w-xl leading-relaxed">
                        Curated meditations, ambient soundscapes, and AI-crafted sessions written for the way you feel today.
                        No streaks shamed. No noise. Just space to land.
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                        <Link to="/auth?mode=signup" data-testid="landing-cta-start" className="qm-btn-primary inline-flex items-center gap-2">
                            Start practicing <ArrowRight size={16} />
                        </Link>
                        <Link to="/auth" data-testid="landing-cta-signin" className="qm-btn-ghost inline-flex items-center gap-2">
                            I already have an account
                        </Link>
                    </div>
                    <ul className="grid sm:grid-cols-3 gap-4 pt-6">
                        {[
                            { icon: Wind, title: 'Curated library', body: 'Hand-picked meditations for stress, sleep, focus.' },
                            { icon: Sparkles, title: 'AI-crafted sessions', body: 'A meditation written for your day, in seconds.' },
                            { icon: Heart, title: 'Self-visualization', body: 'Imagine the version of you that you’re becoming.' },
                        ].map((f) => (
                            <li key={f.title} className="qm-card p-5">
                                <f.icon className="text-forest" size={22} />
                                <div className="mt-3 font-medium text-forest">{f.title}</div>
                                <div className="text-sm text-forest-soft mt-1">{f.body}</div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative grid place-items-center">
                    <div className="absolute inset-0 rounded-[36px] bg-white/40 backdrop-blur-xl border border-white/60 shadow-xl" />
                    <div className="relative py-16">
                        <BreathingCircle size={300} label="Inhale ... Exhale" />
                    </div>
                </div>
            </main>

            <footer className="relative z-10 max-w-6xl mx-auto px-6 pb-2 text-xs text-forest-soft">
                <LegalFooter />
            </footer>
        </div>
    );
}
